import { useCallback, useEffect, useRef, useState } from 'react';
import type { CropperRef, Coordinates } from 'react-advanced-cropper';
import showToast from '../../utils/toastUtils';
import type { FreeCropHandle } from '../../types/freeCrop';
import type { PerspectiveCropperHandle } from '../../types/perspectiveCropper';
import type { UploadReceiptEditMode } from '../../types/uploadReceiptDialog';

const clampRotationValue = (value: number) => Math.max(-180, Math.min(180, value));

type UseUploadReceiptCropperParams = {
  selectedFile: File | null;
  previewUrl: string;
  onError: (message: string) => void;
};

export const useUploadReceiptCropper = ({ selectedFile, previewUrl, onError }: UseUploadReceiptCropperParams) => {
  const [editMode, setEditMode] = useState<UploadReceiptEditMode>('none');
  const [imageMetadata, setImageMetadata] = useState<{ width: number; height: number } | null>(null);
  const [modeSwitching, setModeSwitching] = useState(false);
  const [perspectiveLoading, setPerspectiveLoading] = useState(false);
  const [perspectiveKey, setPerspectiveKey] = useState(0);
  const [horizontalTilt, setHorizontalTilt] = useState(0);
  const [verticalTilt, setVerticalTilt] = useState(0);
  const [rotation, setRotation] = useState<number>(0);
  const [freeCropPreviewBlob, setFreeCropPreviewBlob] = useState<Blob | null>(null);

  const cropperRef = useRef<CropperRef | null>(null);
  const coordinatesRef = useRef<Coordinates | null>(null);
  const perspectiveCropperRef = useRef<PerspectiveCropperHandle | null>(null);
  const freeCropRef = useRef<FreeCropHandle | null>(null);

  const getRadianAngle = useCallback((degreeValue: number) => (degreeValue * Math.PI) / 180, []);

  const retainCoordinates = useCallback(() => {
    if (cropperRef.current) {
      coordinatesRef.current = cropperRef.current.getCoordinates();
    }
  }, []);

  const updateRotation = useCallback(
    (value: number) => {
      retainCoordinates();
      setRotation(clampRotationValue(value));
    },
    [retainCoordinates],
  );

  const createImage = useCallback((url: string): Promise<HTMLImageElement> => {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', (e: Event) => reject(e));
      image.setAttribute('crossOrigin', 'anonymous');
      image.src = url;
    });
  }, []);

  const resetCropperState = useCallback((options?: { bumpKey?: boolean }) => {
    coordinatesRef.current = null;
    setRotation(0);
    setImageMetadata(null);
    if (options?.bumpKey === false) {
      setPerspectiveKey(0);
    } else {
      setPerspectiveKey((key) => key + 1);
    }
    setHorizontalTilt(0);
    setVerticalTilt(0);
    setFreeCropPreviewBlob(null);
    freeCropRef.current?.reset();
  }, []);

  const handlePerspectiveState = useCallback((state: { loading?: boolean }) => {
    setPerspectiveLoading(Boolean(state?.loading));
  }, []);

  const onCropperChange = useCallback(
    (cropper: CropperRef) => {
      if (editMode !== 'tilt') {
        return;
      }
      coordinatesRef.current = cropper.getCoordinates();
    },
    [editMode],
  );

  const handleHorizontalTiltChange = useCallback(
    (_: Event, value: number | number[]) => {
      const v = Array.isArray(value) ? value[0] : value;
      setHorizontalTilt(v);
      perspectiveCropperRef.current?.setHorizontalTilt(v);
    },
    [],
  );

  const handleVerticalTiltChange = useCallback(
    (_: Event, value: number | number[]) => {
      const v = Array.isArray(value) ? value[0] : value;
      setVerticalTilt(v);
      perspectiveCropperRef.current?.setVerticalTilt(v);
    },
    [],
  );

  const onRotationChange = useCallback(
    (_: Event, value: number | number[]) => {
      const r = Array.isArray(value) ? value[0] : value;
      updateRotation(r);
      perspectiveCropperRef.current?.setRotation(r);
    },
    [updateRotation],
  );

  const onRotationDecrease = useCallback(() => {
    onRotationChange({} as Event, rotation - 1);
  }, [onRotationChange, rotation]);

  const onRotationIncrease = useCallback(() => {
    onRotationChange({} as Event, rotation + 1);
  }, [onRotationChange, rotation]);

  const onHorizontalTiltDecrease = useCallback(() => {
    handleHorizontalTiltChange({} as Event, horizontalTilt - 0.05);
  }, [handleHorizontalTiltChange, horizontalTilt]);

  const onHorizontalTiltIncrease = useCallback(() => {
    handleHorizontalTiltChange({} as Event, horizontalTilt + 0.05);
  }, [handleHorizontalTiltChange, horizontalTilt]);

  const onVerticalTiltDecrease = useCallback(() => {
    handleVerticalTiltChange({} as Event, verticalTilt - 0.05);
  }, [handleVerticalTiltChange, verticalTilt]);

  const onVerticalTiltIncrease = useCallback(() => {
    handleVerticalTiltChange({} as Event, verticalTilt + 0.05);
  }, [handleVerticalTiltChange, verticalTilt]);

  const handleFreeCropPreview = useCallback(async () => {
    if (!freeCropRef.current) {
      const message = 'Crop editor is not ready yet';
      onError(message);
      showToast.error(message);
      return;
    }

    try {
      const blob = await freeCropRef.current.preview();
      setFreeCropPreviewBlob(blob);
      freeCropRef.current.showPreview();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to generate preview';
      onError(message);
      showToast.error(message);
    }
  }, [onError]);

  const handleFreeCropEdit = useCallback(() => {
    if (freeCropRef.current) {
      freeCropRef.current.hidePreview();
      setFreeCropPreviewBlob(null);
    }
  }, []);

  const handleFreeCropRevert = useCallback(() => {
    if (freeCropRef.current) {
      freeCropRef.current.revertToInitial();
      setFreeCropPreviewBlob(null);
    }
  }, []);

  const useOriginalImage = useCallback(() => {
    resetCropperState();
    setEditMode('none');
  }, [resetCropperState]);

  const startCornerCrop = useCallback(() => {
    if (editMode === 'none') {
      resetCropperState();
    } else {
      setFreeCropPreviewBlob(null);
      freeCropRef.current?.hidePreview();
    }
    setEditMode('free');
  }, [editMode, resetCropperState]);

  const startPerspectiveEdit = useCallback(() => {
    if (editMode === 'none') {
      resetCropperState();
    } else {
      setFreeCropPreviewBlob(null);
      freeCropRef.current?.hidePreview();
      setPerspectiveKey((key) => key + 1);
    }
    setEditMode('perspective');
  }, [editMode, resetCropperState]);

  const startRotateEdit = useCallback(() => {
    if (editMode === 'none') {
      resetCropperState();
    } else {
      setFreeCropPreviewBlob(null);
      freeCropRef.current?.hidePreview();
    }
    setEditMode('tilt');
  }, [editMode, resetCropperState]);

  const getPerspectiveCroppedImg = useCallback(async (): Promise<File> => {
    if (!selectedFile) {
      throw new Error('No file selected');
    }
    if (!perspectiveCropperRef.current) {
      throw new Error('Perspective editor is not ready yet');
    }

    const blob = await perspectiveCropperRef.current.done();
    return new File([blob], selectedFile.name, {
      type: selectedFile.type,
      lastModified: Date.now(),
    });
  }, [selectedFile]);

  const getFreeCroppedImg = useCallback(async (): Promise<File> => {
    if (!selectedFile) {
      throw new Error('No file selected');
    }
    if (!freeCropRef.current) {
      throw new Error('Crop editor is not ready yet');
    }

    let blob = freeCropRef.current.getSavedImage();
    if (!blob) {
      blob = await freeCropRef.current.done();
    }

    return new File([blob], selectedFile.name, {
      type: selectedFile.type,
      lastModified: Date.now(),
    });
  }, [selectedFile]);

  const getCroppedImg = useCallback(async (): Promise<File> => {
    if (!selectedFile) throw new Error('No file selected');

    const image = await createImage(previewUrl);
    const imageWidth = image.naturalWidth || image.width;
    const imageHeight = image.naturalHeight || image.height;

    const coords = coordinatesRef.current;
    if (!coords) {
      return selectedFile;
    }

    const normalizedRotation = ((rotation % 360) + 360) % 360;
    const isNoRotation = normalizedRotation === 0;

    if (
      isNoRotation &&
      Math.abs(coords.left) < 1 &&
      Math.abs(coords.top) < 1 &&
      Math.abs(coords.width - imageWidth) < 1 &&
      Math.abs(coords.height - imageHeight) < 1
    ) {
      return selectedFile;
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) return selectedFile;

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    canvas.width = Math.round(coords.width);
    canvas.height = Math.round(coords.height);

    if (isNoRotation) {
      ctx.drawImage(
        image,
        coords.left,
        coords.top,
        coords.width,
        coords.height,
        0,
        0,
        coords.width,
        coords.height,
      );
    } else {
      const rotRad = getRadianAngle(rotation);
      const cos = Math.abs(Math.cos(rotRad));
      const sin = Math.abs(Math.sin(rotRad));
      const rotatedWidth = imageWidth * cos + imageHeight * sin;
      const rotatedHeight = imageWidth * sin + imageHeight * cos;

      const tempCanvas = document.createElement('canvas');
      const tempCtx = tempCanvas.getContext('2d', { alpha: false });
      if (!tempCtx) return selectedFile;

      tempCtx.imageSmoothingEnabled = true;
      tempCtx.imageSmoothingQuality = 'high';

      tempCanvas.width = rotatedWidth;
      tempCanvas.height = rotatedHeight;
      tempCtx.fillStyle = 'white';
      tempCtx.fillRect(0, 0, rotatedWidth, rotatedHeight);
      tempCtx.translate(rotatedWidth / 2, rotatedHeight / 2);
      tempCtx.rotate(rotRad);
      tempCtx.drawImage(image, -imageWidth / 2, -imageHeight / 2);

      const centerX = rotatedWidth / 2;
      const centerY = rotatedHeight / 2;
      const cropCenterX = coords.left + coords.width / 2;
      const cropCenterY = coords.top + coords.height / 2;
      const rotatedCropX = centerX + (cropCenterX - imageWidth / 2) * cos - (cropCenterY - imageHeight / 2) * sin;
      const rotatedCropY = centerY + (cropCenterX - imageWidth / 2) * sin + (cropCenterY - imageHeight / 2) * cos;

      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, coords.width, coords.height);
      ctx.drawImage(
        tempCanvas,
        rotatedCropX - coords.width / 2,
        rotatedCropY - coords.height / 2,
        coords.width,
        coords.height,
        0,
        0,
        coords.width,
        coords.height,
      );
    }

    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          if (blob) {
            const croppedFile = new File([blob], selectedFile.name, {
              type: selectedFile.type,
              lastModified: Date.now(),
            });
            resolve(croppedFile);
          } else {
            resolve(selectedFile);
          }
        },
        selectedFile.type,
        0.95,
      );
    });
  }, [selectedFile, previewUrl, rotation, createImage, getRadianAngle]);

  useEffect(() => {
    let isCancelled = false;

    if (!previewUrl || editMode !== 'tilt') {
      setImageMetadata(null);
      return;
    }

    createImage(previewUrl)
      .then((image) => {
        if (!isCancelled) {
          setImageMetadata({
            width: image.naturalWidth || image.width,
            height: image.naturalHeight || image.height,
          });
        }
      })
      .catch(() => {
        if (!isCancelled) {
          setImageMetadata(null);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [previewUrl, editMode, createImage]);

  useEffect(() => {
    if (editMode !== 'tilt') {
      return;
    }

    if (!previewUrl || !cropperRef.current || !imageMetadata) {
      return;
    }

    const timer = setTimeout(() => {
      const coords =
        coordinatesRef.current ?? {
          left: 0,
          top: 0,
          width: imageMetadata.width,
          height: imageMetadata.height,
        };

      cropperRef.current?.setCoordinates(coords);
    }, 200);

    return () => clearTimeout(timer);
  }, [previewUrl, imageMetadata, editMode, rotation]);

  useEffect(() => {
    if (editMode === 'perspective') {
      perspectiveCropperRef.current?.setRotation(rotation);
    }
  }, [editMode, rotation]);

  return {
    editMode,
    modeSwitching,
    perspectiveLoading,
    perspectiveKey,
    horizontalTilt,
    verticalTilt,
    rotation,
    freeCropPreviewBlob,
    cropperRef,
    perspectiveCropperRef,
    freeCropRef,
    setEditMode,
    setModeSwitching,
    setPerspectiveLoading,
    resetCropperState,
    handlePerspectiveState,
    onCropperChange,
    onRotationChange,
    onRotationDecrease,
    onRotationIncrease,
    handleHorizontalTiltChange,
    onHorizontalTiltDecrease,
    onHorizontalTiltIncrease,
    handleVerticalTiltChange,
    onVerticalTiltDecrease,
    onVerticalTiltIncrease,
    handleFreeCropPreview,
    handleFreeCropEdit,
    handleFreeCropRevert,
    useOriginalImage,
    startCornerCrop,
    startPerspectiveEdit,
    startRotateEdit,
    getPerspectiveCroppedImg,
    getFreeCroppedImg,
    getCroppedImg,
  };
};
